/**
 * Available permissions in the system
 */

import { PermissionCategory } from '../types/rolePermission'

export interface AvailablePermission {
  permission: string
  resource: string
  action: string
  category: PermissionCategory
  description: string
}

/**
 * All available permissions organized by category
 */
export const AVAILABLE_PERMISSIONS: AvailablePermission[] = [
  // Patients
  {
    permission: 'patients:read',
    resource: 'patients',
    action: 'read',
    category: 'Patients',
    description: 'View patient information',
  },
  {
    permission: 'patients:write',
    resource: 'patients',
    action: 'write',
    category: 'Patients',
    description: 'Create and edit patients',
  },
  {
    permission: 'patients:delete',
    resource: 'patients',
    action: 'delete',
    category: 'Patients',
    description: 'Delete patients',
  },
  // Field-level patient permissions
  {
    permission: 'patients:read:medications',
    resource: 'patients',
    action: 'read:medications',
    category: 'Patients',
    description: 'Access to patient medication details',
  },
  {
    permission: 'patients:read:conditions',
    resource: 'patients',
    action: 'read:conditions',
    category: 'Patients',
    description: 'Access to patient condition details',
  },
  {
    permission: 'patients:read:sensitive-documents',
    resource: 'patients',
    action: 'read:sensitive-documents',
    category: 'Patients',
    description: 'Access to sensitive patient documents',
  },
  {
    permission: 'patients:read:vital-signs',
    resource: 'patients',
    action: 'read:vital-signs',
    category: 'Patients',
    description: 'Access to patient vital signs data',
  },
  {
    permission: 'patients:read:lab-results',
    resource: 'patients',
    action: 'read:lab-results',
    category: 'Patients',
    description: 'Access to patient lab results',
  },
  {
    permission: 'patients:read:allergies',
    resource: 'patients',
    action: 'read:allergies',
    category: 'Patients',
    description: 'Access to patient allergy information',
  },
  {
    permission: 'patients:read:family-history',
    resource: 'patients',
    action: 'read:family-history',
    category: 'Patients',
    description: 'Access to patient family history',
  },
  {
    permission: 'patients:read:full-access',
    resource: 'patients',
    action: 'read:full-access',
    category: 'Patients',
    description: 'Full access to all patient data including highly confidential fields',
  },
  {
    permission: 'patients:override:emergency',
    resource: 'patients',
    action: 'override:emergency',
    category: 'Patients',
    description: 'Permission to request and approve emergency access overrides',
  },
  // Reminders
  {
    permission: 'reminders:read',
    resource: 'reminders',
    action: 'read',
    category: 'Patients',
    description: 'View patient reminders',
  },
  {
    permission: 'reminders:write',
    resource: 'reminders',
    action: 'write',
    category: 'Patients',
    description: 'Create and edit patient reminders',
  },
  {
    permission: 'reminder-rules:read',
    resource: 'reminder-rules',
    action: 'read',
    category: 'Settings',
    description: 'View reminder rules',
  },
  {
    permission: 'reminder-rules:write',
    resource: 'reminder-rules',
    action: 'write',
    category: 'Settings',
    description: 'Create and edit reminder rules',
  },
  
  // Appointments
  {
    permission: 'appointments:read',
    resource: 'appointments',
    action: 'read',
    category: 'Appointments',
    description: 'View appointments',
  },
  {
    permission: 'appointments:write',
    resource: 'appointments',
    action: 'write',
    category: 'Appointments',
    description: 'Create and edit appointments',
  },
  {
    permission: 'appointments:delete',
    resource: 'appointments',
    action: 'delete',
    category: 'Appointments',
    description: 'Delete appointments',
  },
  
  // Templates
  {
    permission: 'templates:read',
    resource: 'templates',
    action: 'read',
    category: 'Templates',
    description: 'View templates',
  },
  {
    permission: 'templates:write',
    resource: 'templates',
    action: 'write',
    category: 'Templates',
    description: 'Create and edit templates',
  },
  {
    permission: 'templates:delete',
    resource: 'templates',
    action: 'delete',
    category: 'Templates',
    description: 'Delete templates',
  },
  
  // Lab Results
  {
    permission: 'lab-results:read',
    resource: 'lab-results',
    action: 'read',
    category: 'Lab Results',
    description: 'View lab results',
  },
  {
    permission: 'lab-results:write',
    resource: 'lab-results',
    action: 'write',
    category: 'Lab Results',
    description: 'Create and edit lab results',
  },

  // Billing
  {
    permission: 'billing-accounts:read',
    resource: 'billing-accounts',
    action: 'read',
    category: 'Billing',
    description: 'View billing accounts and balances',
  },
  {
    permission: 'billing-accounts:write',
    resource: 'billing-accounts',
    action: 'write',
    category: 'Billing',
    description: 'Create and update billing accounts',
  },
  {
    permission: 'billing-items:read',
    resource: 'billing-items',
    action: 'read',
    category: 'Billing',
    description: 'View billable items',
  },
  {
    permission: 'billing-items:write',
    resource: 'billing-items',
    action: 'write',
    category: 'Billing',
    description: 'Create and update billable items',
  },
  {
    permission: 'billing-payments:read',
    resource: 'billing-payments',
    action: 'read',
    category: 'Billing',
    description: 'View payments and payment history',
  },
  {
    permission: 'billing-payments:write',
    resource: 'billing-payments',
    action: 'write',
    category: 'Billing',
    description: 'Record payments and refunds',
  },
  {
    permission: 'billing-statements:read',
    resource: 'billing-statements',
    action: 'read',
    category: 'Billing',
    description: 'View billing statements',
  },
  {
    permission: 'billing-statements:write',
    resource: 'billing-statements',
    action: 'write',
    category: 'Billing',
    description: 'Generate and issue billing statements',
  },
  
  // Users
  {
    permission: 'users:read',
    resource: 'users',
    action: 'read',
    category: 'Users',
    description: 'View users',
  },
  {
    permission: 'users:write',
    resource: 'users',
    action: 'write',
    category: 'Users',
    description: 'Create and edit users',
  },
  {
    permission: 'users:delete',
    resource: 'users',
    action: 'delete',
    category: 'Users',
    description: 'Delete users',
  },
  
  // Settings
  {
    permission: 'settings:read',
    resource: 'settings',
    action: 'read',
    category: 'Settings',
    description: 'View settings',
  },
  {
    permission: 'settings:write',
    resource: 'settings',
    action: 'write',
    category: 'Settings',
    description: 'Modify settings',
  },
  
  // Role Permissions
  {
    permission: 'role-permissions:read',
    resource: 'role-permissions',
    action: 'read',
    category: 'Role Permissions',
    description: 'View role permissions',
  },
  {
    permission: 'role-permissions:write',
    resource: 'role-permissions',
    action: 'write',
    category: 'Role Permissions',
    description: 'Modify role permissions',
  },
]

/**
 * All permissions permission (wildcard)
 */
export const ALL_PERMISSIONS: AvailablePermission = {
  permission: '*',
  resource: '*',
  action: '*',
  category: 'System',
  description: 'All permissions (administrative access)',
}

/**
 * Group permissions by category
 */
export function getPermissionsByCategory(): Record<PermissionCategory, AvailablePermission[]> {
  const grouped: Record<PermissionCategory, AvailablePermission[]> = {
    'Patients': [],
    'Appointments': [],
    'Templates': [],
    'Lab Results': [],
    'Billing': [],
    'Users': [],
    'Settings': [],
    'Role Permissions': [],
    'System': [],
  }
  
  AVAILABLE_PERMISSIONS.forEach((perm) => {
    grouped[perm.category].push(perm)
  })
  
  return grouped
}
